import { useLanguageStore } from '@/i18n/languageStore';
import { api } from '@/api/client';
import { canTakeCalls } from '@/calls/CallProvider';
import { getConfig, type KindraConfig } from '@/config/config';
import { registerBeforeSignOut } from '@/state/sessionStore';

/**
 * Browser push (tasks.md T179; research.md §10): Firebase Cloud Messaging for
 * Web, loaded only when push is used. The token is sent as a `web`
 * registration, so the member's phone keeps its own. While a Kindra tab is
 * open the sockets deliver everything; push only matters when none is.
 */
export type PushState =
  /** No service worker or push in this browser. */
  | 'unsupported'
  /** This Kindra server has no Firebase web configuration. */
  | 'unconfigured'
  /** iPhone/iPad Safari: push works only from the Home Screen app. */
  | 'needs-install'
  | NotificationPermission;

export const SERVICE_WORKER_URL = '/firebase-messaging-sw.js';
const TOKEN_KEY = 'kindra.push.token';

function pushConfig(): Required<Pick<KindraConfig, 'firebase' | 'vapidKey'>> | null {
  try {
    const { firebase, vapidKey } = getConfig();
    return firebase && vapidKey ? { firebase, vapidKey } : null;
  } catch {
    return null;
  }
}

function isIosBrowserTab(): boolean {
  const nav = navigator as Navigator & { standalone?: boolean };
  return /iPhone|iPad|iPod/.test(nav.userAgent) && nav.standalone !== true;
}

export function pushState(): PushState {
  if (!pushConfig()) return 'unconfigured';
  if (isIosBrowserTab()) return 'needs-install';
  if (
    typeof navigator === 'undefined' ||
    !('serviceWorker' in navigator) ||
    typeof PushManager === 'undefined' ||
    typeof Notification === 'undefined'
  ) {
    return 'unsupported';
  }
  return Notification.permission;
}

function readToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

function writeToken(token: string | null): void {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {
    // not remembering it only means sign-out cannot name it
  }
}

async function messaging() {
  const settings = pushConfig();
  if (!settings) return null;
  const [{ initializeApp, getApps, getApp }, firebaseMessaging] = await Promise.all([
    import('firebase/app'),
    import('firebase/messaging'),
  ]);
  if (!(await firebaseMessaging.isSupported())) return null;
  const app = getApps().length > 0 ? getApp() : initializeApp(settings.firebase);
  return { instance: firebaseMessaging.getMessaging(app), api: firebaseMessaging, settings };
}

/**
 * Registers this browser for push once notifications are allowed: the service
 * worker, a Firebase token, and the token sent to Kindra. Sent again on every
 * start, so a token Firebase replaced takes over. False when it could not.
 */
export async function registerPush(): Promise<boolean> {
  if (pushState() !== 'granted') return false;
  try {
    const fcm = await messaging();
    if (!fcm) return false;
    const serviceWorkerRegistration = await navigator.serviceWorker.register(SERVICE_WORKER_URL);
    const token = await fcm.api.getToken(fcm.instance, {
      vapidKey: fcm.settings.vapidKey,
      serviceWorkerRegistration,
    });
    if (!token) return false;
    // Calls ring only browsers that can take them (specs/003 #19/#23).
    await api.patch('/accounts/me/push-token', {
      token,
      platform: 'web',
      callCapable: canTakeCalls(),
      appVersion: __APP_VERSION__,
      language: useLanguageStore.getState().language,
    });
    writeToken(token);
    return true;
  } catch {
    return false;
  }
}

/** Asks for permission (from a click), then registers. */
export async function enablePush(): Promise<{ permission: PushState; registered: boolean }> {
  const state = pushState();
  if (state !== 'default' && state !== 'granted') return { permission: state, registered: false };
  const permission = state === 'granted' ? state : await Notification.requestPermission();
  return { permission, registered: permission === 'granted' ? await registerPush() : false };
}

/** Sign-out: Kindra stops pushing to this browser, and the Firebase token is dropped. */
export async function unregisterPush(): Promise<void> {
  const token = readToken();
  if (!token) return;
  writeToken(null);
  await api.delete('/accounts/me/push-token', { token }).catch(() => undefined);
  try {
    const fcm = await messaging();
    if (fcm) await fcm.api.deleteToken(fcm.instance);
  } catch {
    // the server no longer uses it; nothing else to do
  }
}

/** While signed in: registers when already allowed, and unregisters before sign-out. */
export function startPush(): () => void {
  const stopHook = registerBeforeSignOut(unregisterPush);
  void registerPush();
  // Notifications are written in the language chosen in the app, so the server is told when it changes.
  const stopLanguage = useLanguageStore.subscribe((state, previous) => {
    if (state.language !== previous.language) void registerPush();
  });
  return () => {
    stopLanguage();
    stopHook();
  };
}
